import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import moment from 'moment';
import {
  Card,
  CardContent,
  CardActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TableContainer,
  Paper,
  Typography,
  TextField,
  Grid,
  Button,
  Skeleton,
  MenuItem,
  Chip,
  Box,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';

const useStyles = makeStyles((theme) => ({
  content: { padding: 0 },
  filters: {
    padding: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    alignItems: 'flex-end',
  },
  actions: { justifyContent: 'space-between' },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
}));

const PHASES = ['pending', 'running', 'done', 'error', 'expired', 'rateLimited'];
const TYPES = ['fetchTransactions', 'captureScreenshot', 'exportPrinterSettings', 'importMenu'];

const AuditTable = ({ audits, loading }) => {
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [filterDeviceId, setFilterDeviceId] = useState('');
  const [filterActor, setFilterActor] = useState('');
  const [filterPhase, setFilterPhase] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  const filtered = useMemo(() => {
    if (!audits) return [];
    return audits.filter((a) => {
      if (filterDeviceId && !a.deviceId?.toLowerCase().includes(filterDeviceId.toLowerCase()))
        return false;
      if (filterActor && !a.actor?.toLowerCase().includes(filterActor.toLowerCase()))
        return false;
      if (filterPhase && a.phase !== filterPhase) return false;
      if (filterType && a.type !== filterType) return false;
      if (filterStartDate) {
        const ts = new Date(a.timestamp).getTime();
        if (ts < new Date(filterStartDate).getTime()) return false;
      }
      if (filterEndDate) {
        const ts = new Date(a.timestamp).getTime();
        const end = new Date(filterEndDate);
        end.setDate(end.getDate() + 1);
        if (ts >= end.getTime()) return false;
      }
      return true;
    });
  }, [audits, filterDeviceId, filterActor, filterPhase, filterType, filterStartDate, filterEndDate]);

  const handleExportCSV = () => {
    const headers = ['Timestamp', 'Actor', 'Device ID', 'Type', 'Phase', 'Detail'];
    const rows = filtered.map((a) => [
      a.timestamp ? new Date(a.timestamp).toISOString() : '',
      a.actor || '',
      a.deviceId || '',
      a.type || '',
      a.phase || '',
      a.detail || '',
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit-export-${moment().format('YYYY-MM-DD')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} height={48} sx={{ mb: 1 }} />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <div className={classes.filters}>
        <TextField
          label="Device ID"
          size="small"
          value={filterDeviceId}
          onChange={(e) => { setFilterDeviceId(e.target.value); setPage(0); }}
          inputProps={{ 'aria-label': 'Filter by device ID' }}
        />
        <TextField
          label="Actor"
          size="small"
          value={filterActor}
          onChange={(e) => { setFilterActor(e.target.value); setPage(0); }}
          inputProps={{ 'aria-label': 'Filter by actor email' }}
        />
        <TextField
          label="Phase"
          size="small"
          select
          value={filterPhase}
          onChange={(e) => { setFilterPhase(e.target.value); setPage(0); }}
          sx={{ minWidth: 130 }}
          inputProps={{ 'aria-label': 'Filter by phase' }}
        >
          <MenuItem value="">All</MenuItem>
          {PHASES.map((p) => (
            <MenuItem key={p} value={p}>{p}</MenuItem>
          ))}
        </TextField>
        <TextField
          label="Type"
          size="small"
          select
          value={filterType}
          onChange={(e) => { setFilterType(e.target.value); setPage(0); }}
          sx={{ minWidth: 170 }}
          inputProps={{ 'aria-label': 'Filter by type' }}
        >
          <MenuItem value="">All</MenuItem>
          {TYPES.map((t) => (
            <MenuItem key={t} value={t}>{t}</MenuItem>
          ))}
        </TextField>
        <TextField
          label="Start date"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={filterStartDate}
          onChange={(e) => { setFilterStartDate(e.target.value); setPage(0); }}
          inputProps={{ 'aria-label': 'Filter from date' }}
        />
        <TextField
          label="End date"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={filterEndDate}
          onChange={(e) => { setFilterEndDate(e.target.value); setPage(0); }}
          inputProps={{ 'aria-label': 'Filter to date' }}
        />
      </div>

      <CardContent className={classes.content}>
        {filtered.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography variant="body1" color="textSecondary">
              No audit events match the current filters.
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} elevation={0}>
            <Table aria-label="Audit feed">
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Actor</TableCell>
                  <TableCell>Device ID</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Phase</TableCell>
                  <TableCell>Detail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((audit, idx) => (
                    <TableRow key={audit.id || idx}>
                      <TableCell>
                        {audit.timestamp
                          ? moment(audit.timestamp).format('DD/MM/YYYY HH:mm:ss')
                          : '—'}
                      </TableCell>
                      <TableCell>{audit.actor || '—'}</TableCell>
                      <TableCell>{audit.deviceId || '—'}</TableCell>
                      <TableCell>
                        <Chip label={audit.type} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={audit.phase}
                          size="small"
                          color={
                            audit.phase === 'done'
                              ? 'success'
                              : audit.phase === 'error'
                                ? 'error'
                                : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                          {audit.detail || '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      <CardActions className={classes.actions}>
        <Button
          startIcon={<DownloadIcon />}
          onClick={handleExportCSV}
          disabled={filtered.length === 0}
          size="small"
          aria-label="Export filtered audit as CSV"
        >
          Export CSV
        </Button>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[20, 50, 100]}
        />
      </CardActions>
    </Card>
  );
};

AuditTable.propTypes = {
  audits: PropTypes.array,
  loading: PropTypes.bool,
};

export default AuditTable;
